import { ProductInfo } from "@insite/client-framework/Common/ProductInfo";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { HasShellContext } from "@insite/client-framework/Components/IsInShell";
import { ProductContext, ProductContextModel } from "@insite/client-framework/Components/ProductContext";
import { HasProductContext, withProductContext } from "@insite/client-framework/Components/ProductContext";
import { makeHandlerChainAwaitable } from "@insite/client-framework/HandlerCreator";
import siteMessage from "@insite/client-framework/SiteMessage";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import addToCart from "@insite/client-framework/Store/Pages/Cart/Handlers/AddToCart";
import translate from "@insite/client-framework/Translate";
import { CartLineModel } from "@insite/client-framework/Types/ApiModels";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import ProductAddedToCartMessage from "@insite/content-library/Components/ProductAddedToCartMessage";
import ProductAddToCartButton from "@insite/content-library/Components/ProductAddToCartButton";
import ProductQuantityOrdered from "@insite/content-library/Components/ProductQuantityOrdered";
import SearchTextField, { SearchTextFieldStyles } from "@insite/content-library/Widgets/OrderHistory/SearchTextField";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import DataTable, { DataTableProps } from "@insite/mobius/DataTable/DataTable";
import DataTableBody from "@insite/mobius/DataTable/DataTableBody";
import DataTableCell, { DataTableCellProps } from "@insite/mobius/DataTable/DataTableCell";
import DataTableHead from "@insite/mobius/DataTable/DataTableHead";
import DataTableHeader, { DataTableHeaderProps } from "@insite/mobius/DataTable/DataTableHeader";
import DataTableRow from "@insite/mobius/DataTable/DataTableRow";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import Link from "@insite/mobius/Link/Link";
import TextField, { TextFieldProps } from "@insite/mobius/TextField";
import ToasterContext from "@insite/mobius/Toast/ToasterContext";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import { breakpointMediaQueries } from "@insite/mobius/utilities";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { OrdersModel, UserDetailsModel } from "@theStellarBlueprint/CustomApiModels";
import CustomApplicationState from "@theStellarBlueprint/Store/CustomApplicationState";
import loadPurchasedItems from "@theStellarBlueprint/Store/Data/PurchasedItems/Handlers/LoadPurchasedItems";
import { getPurchasedItemsData } from "@theStellarBlueprint/Store/Data/PurchasedItems/LoadPurchasedItemsSelector";
import React, { ChangeEvent, useContext, useEffect, useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css, ThemeProps } from "styled-components";

const mapStateToProps = (state: CustomApplicationState & HasProductContext) => ({
    purchasedItemsData: getPurchasedItemsData(state),
    productSettings: getSettingsCollection(state).productSettings,
});

const mapDispatchToProps = {
    loadPurchasedItems,
    addToCart: makeHandlerChainAwaitable(addToCart),
};

type Props = ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps> & HasProductContext;

export interface OrderHistoryTableStyles {
    recentlyPurchasedTableWrapper?: InjectableCss;
    orderHistoryItem?: InjectableCss;
    clearSearch?: InjectableCss;
    searchQuery?: InjectableCss;
    description?: DataTableCellProps;
    descriptionHeader?: DataTableCellProps;
    dataTable?: DataTableProps;
    noResultsContainer?: InjectableCss;
    noResultsText?: TypographyProps;
    productQtyOrdered?: TextFieldProps;
}

export const orderHistoryTableStyles: OrderHistoryTableStyles = {
    recentlyPurchasedTableWrapper: {
        css: css`
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        overflow-x: scroll;
                    `,
                    css`
                        overflow-x: scroll;
                    `,
                    css`
                        overflow-x: scroll;
                    `,
                    css`
                        overflow-x: scroll;
                    `,
                    css`
                    overflow-x: scroll;
                `,
                ])}
        `,
    },
    dataTable: {
        css: css`
            th,
            td {
                padding: 0 20px;
            }
        `,
    },
    // orderHistoryItem: {
    //     css: css`
    //         overflow: auto;
    //     `,
    // },
    clearSearch: {
        css: css`
            display: flex;
            margin-bottom: 14px;
            justify-content: space-between;
        `,
    },
    searchQuery: {
        css: css`
            margin-top: 12px;
            font-size: 14px;
        `,
    },
    productQtyOrdered: {
        css: css`
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        width: 50px !important;
                    `,
                    css`
                        width: 50px !important;
                    `,
                    css`
                        width: 50px !important;
                    `,
                    css`
                        width: 70px !important;
                    `,
                    css`
                        width: 70px !important;
                    `,
                ])}
        `,
    },
    description: {
        typographyProps: {
            ellipsis: true,
            css: css`
                display: block;
                max-width: 450px;
            `,
        },
    },
    noResultsContainer: {
        css: css`
            text-align: center;
            padding: 20px;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    null,
                    null,
                    null,
                    css`
                        [data-test-selector="PurchasedItemsNoneFound"] {
                            margin-top: 50px;
                        }
                    `,
                    null,
                ])}
        `,
    },
    noResultsText: {
        variant: "h4",
    },
};

const styles = orderHistoryTableStyles;
const PurchasedItems: React.FC<Props> = ({ purchasedItemsData, productSettings, addToCart }) => {
    const UserDetailsData: UserDetailsModel | undefined = purchasedItemsData?.value;
    const purchasedItems: OrdersModel[] | undefined = UserDetailsData?.orders;
    const toasterContext = useContext(ToasterContext);
    const [purchasedItemModifyArr, setPurchasedItemModifyArr] = useState<any[]>([]);

    useEffect(() => {
        const purchasedItemArr: any[] = [];

        if (purchasedItems) {
            purchasedItems?.map((value, index) => {
                purchasedItemArr.push({
                    productId: value.productId,
                    productNumber: value.customerProductNumber,
                    description: value.description,
                    isDiscontinued: value.isDiscontinued,
                    productErpNumber: value.productErpNumber,
                    qtyOnHand: value.qtyOnHand,
                    unitNetPrice: value.unitNetPrice,
                    unitOfMeasure: value.unitOfMeasure,
                    inputQty: 1,
                });
            });
            setPurchasedItemModifyArr(purchasedItemArr);
        }
    }, [purchasedItems]);

    if (purchasedItems?.length === 0) {
        return (
            <StyledWrapper>
                <StyledWrapper {...styles.noResultsContainer}>
                    <Typography as="p" {...styles.noResultsText} data-test-selector="PurchasedItemsNoneFound">
                        {translate("Purchased Items Not Found")}
                    </Typography>
                </StyledWrapper>
            </StyledWrapper>
        );
    }

    const addToCartClickHandler = async (index: number) => {
        const currePuchasedItemObj = purchasedItemModifyArr[index];

        const cartline = (await addToCart({
            productId: currePuchasedItemObj.productId,
            qtyOrdered: currePuchasedItemObj.inputQty,
            unitOfMeasure: currePuchasedItemObj.unitOfMeasure,
        })) as CartLineModel;

        if (productSettings.showAddToCartConfirmationDialog) {
            toasterContext.addToast({
                body: <ProductAddedToCartMessage isQtyAdjusted={cartline.isQtyAdjusted} multipleProducts={false} />,
                messageType: "success",
                timeoutLength: 10000,
            });
        }
    };
    const qtyChangeHandler = (value: string, index: number) => {
        const updatedValueArr: any[] = [...purchasedItemModifyArr];
        updatedValueArr[index] = { ...updatedValueArr[index], inputQty: value };
        setPurchasedItemModifyArr(updatedValueArr);
    };
    return (
        <StyledWrapper data-test-selector="orderHistoryItem" {...styles.recentlyPurchasedTableWrapper}>
            <DataTable {...styles.dataTable}>
                <DataTableHead>
                    <DataTableHeader>{translate("Stellar Number #")}</DataTableHeader>
                    <DataTableHeader {...styles.descriptionHeader}>{translate("Description")}</DataTableHeader>
                    <DataTableHeader>{translate("Customer Part Number")}</DataTableHeader>
                    <DataTableHeader>{translate("UOM")}</DataTableHeader>
                    <DataTableHeader>{translate("Qty")}</DataTableHeader>
                    <DataTableHeader>{translate("Add To Cart")}</DataTableHeader>
                </DataTableHead>
                <DataTableBody>
                    {purchasedItemModifyArr &&
                        purchasedItemModifyArr?.map((v: any, i: number) => (
                            <DataTableRow key={v.productErpNumber + i}>
                                <DataTableCell>
                                    <Link
                                        href={`/Product/${v.productErpNumber.replace(" ", "-")}`}
                                        data-test-selector="productDescriptionLink"
                                    >
                                        {v.productErpNumber}
                                    </Link>
                                </DataTableCell>
                                <DataTableCell {...styles.description}>{v.description}</DataTableCell>
                                <DataTableCell>{v.productNumber}</DataTableCell>
                                <DataTableCell>{v.unitOfMeasure}</DataTableCell>
                                <DataTableCell>
                                    <TextField
                                        type="number"
                                        min={0}
                                        max={999999}
                                        data-test-selector="product_qtyOrdered"
                                        value={v.inputQty}
                                        {...styles.productQtyOrdered}
                                        onChange={e => qtyChangeHandler(e.target.value, i)}
                                    />
                                </DataTableCell>
                                <DataTableCell>
                                    <Button
                                        onClick={() => addToCartClickHandler(i)}
                                        disabled={
                                            (v.qtyOnHand === 0 && v.isDiscontinued === "TRUE") || Number(v.inputQty) < 1
                                        }
                                    >
                                        {translate("Add to Cart")}
                                    </Button>
                                </DataTableCell>
                            </DataTableRow>
                        ))}
                </DataTableBody>
            </DataTable>
        </StyledWrapper>
    );
};

export default withProductContext(connect(mapStateToProps, mapDispatchToProps)(PurchasedItems));
